import type { Session } from './types';
import { browser } from '$app/environment';
import { get, readonly, writable } from 'svelte/store';

/**
 * Creates an authentication store containing the user session.
 * This store should only be used in a client context and never on the server
 * as it could leak sensitive information and impact caching.
 *
 * To guarantee this, we make sure the refreshSession method is only called on the client.
 * On the server the store content is always null.
 */
export function createAuthStore() {
	const store = writable<Session | null>(null);

	async function refreshSession() {
		if (!browser) {
			throw new Error('auth session refresh must be called on the client');
		}

		let userSession: Session | null = null;
		// Fetch the session from authjs endpoint
		try {
			const sessionResponse = await window.fetch('/auth/session');
			userSession = await sessionResponse.json();
		} catch (error) {
			console.error('Error fetching session', error);
			userSession = null;
		}

		store.set(userSession);
		return userSession;
	}

	function getSession() {
		return get(store);
	}

	return {
		...readonly(store),
		refreshSession,
		getSession,
	};
}

export type AuthStore = ReturnType<typeof createAuthStore>;

let authStore: AuthStore | null = null;

export function getAuthStore() {
	if (!authStore) {
		authStore = createAuthStore();
	}

	return authStore;
}
